import { getEntitiesByFpIdAnnotationsQueryKey, patchEntitiesByFpIdAnnotationsByAnnotationIdMutation } from '@onefootprint/fetch-dashboard';
import { IcoChevronRight16 } from '@onefootprint/icons';
import type { Annotation } from '@onefootprint/request-types/dashboard';
import { Toggle } from '@onefootprint/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { cx } from 'class-variance-authority';
import { format } from 'date-fns';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PermissionGate from '@/components/permission-gate';
import TruncatedText from '@/entity/components/truncated-text';
import useEntityId from '@/entity/hooks/use-entity-id';
import Actor from '../actor';
type AnnotationNoteProps = {
  annotation: Annotation;
  history?: Annotation[];
  hidePinToggle?: boolean;
};
const AnnotationNote = ({
  annotation,
  history,
  hidePinToggle
}: AnnotationNoteProps) => {
  const {
    t
  } = useTranslation('entity-details', {
    keyPrefix: 'auditTrail.timeline.onboardingDecisionEvent.humanDecision'
  });
  const [isNotePinned, setIsNotePinned] = useState(!!annotation?.isPinned);
  const [showHistory, setShowHistory] = useState(false);
  const updateMutation = useMutation(patchEntitiesByFpIdAnnotationsByAnnotationIdMutation({}));
  const queryClient = useQueryClient();
  const entityId = useEntityId();
  const hasHistory = history && history.length > 0;
  useEffect(() => {
    setIsNotePinned(annotation.isPinned);
  }, [annotation?.isPinned]);
  const update = async (isPinned: boolean) => {
    const applyOptimisticUpdate = () => {
      const currentAnnotations = queryClient.getQueryData<Annotation[]>(getEntitiesByFpIdAnnotationsQueryKey({
        path: {
          fpId: entityId
        }
      }));
      if (!currentAnnotations) {
        return;
      }
      queryClient.setQueryData(getEntitiesByFpIdAnnotationsQueryKey({
        path: {
          fpId: entityId
        }
      }), currentAnnotations.map(annotationItem => annotationItem.id === annotation.id ? {
        ...annotationItem,
        isPinned: false
      } : annotationItem));
    };
    const revalidateQueries = () => {
      queryClient.invalidateQueries();
    };
    try {
      applyOptimisticUpdate();
      await updateMutation.mutateAsync({
        path: {
          fpId: entityId,
          annotationId: annotation.id
        },
        body: {
          isPinned
        }
      });
    } catch (error) {
      console.error(error);
    } finally {
      revalidateQueries();
    }
  };
  const handlePinNoteChange = async () => {
    const newIsNotePinned = !isNotePinned;
    setIsNotePinned(newIsNotePinned);
    await update(newIsNotePinned);
  };
  return annotation ? <>
      <div className="relative">
        <TruncatedText text={annotation.note} truncatedTextNumLines={2} className="mb-3 pt-2 pr-3 pb-2 pl-3 bg-secondary rounded text-secondary text-body-2 wrap-anywhere md:wrap-normal" supportMarkdown />
        {hasHistory && <p className="text-caption-1 text-quaternary fp-text absolute top-2 right-3">{t($ => $.edited)}</p>}
      </div>
      {hasHistory && <div className="mb-3 flex flex-col gap-4">
          <button type="button" className="flex items-center gap-1 cursor-pointer text-tertiary hover:text-secondary transition-colors" onClick={() => setShowHistory(prev => !prev)}>
            <div className={cx('flex transition-transform duration-200', {
          'rotate-90': showHistory
        })}>
              <IcoChevronRight16 />
            </div>
            <p className="text-caption-1 text-inherit fp-text">
              {t($_0 => $_0.viewEditHistory, {
            count: history.length
          })}
            </p>
          </button>
          {showHistory && <div className="flex flex-col gap-4 border-l-2 border-tertiary pl-3">
              {history.map(entry => <div key={entry.id} className="flex flex-col gap-2">
                  <div className="flex flex-wrap items-center gap-2 md:flex-nowrap">
                    <Actor actor={entry.source} />
                    <p className="text-caption-1 text-quaternary fp-text">
                      {format(new Date(entry.timestamp), 'MM/dd/yy hh:mm a')}
                    </p>
                  </div>
                  <TruncatedText text={entry.note} truncatedTextNumLines={2} className="pt-1.5 pr-3 pb-1.5 pl-3 bg-primary rounded border border-tertiary text-tertiary text-body-2 wrap-anywhere md:wrap-normal" supportMarkdown />
                </div>)}
            </div>}
        </div>}
      {hidePinToggle ? null : <PermissionGate scopeKind="manual_review" fallbackText="">
          <Toggle checked={isNotePinned} onChange={handlePinNoteChange} size="compact" label={t($_1 => $_1.pinNote)} />
        </PermissionGate>}
    </> : null;
};
export default AnnotationNote;